import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

import { URLS } from '../../resources/constants';
import { useQbSyncApi } from '../../resources/hooks/api/useQbSyncApiHook';
import ReSyncModal from './ReSyncModal';
import { dateTimeLabel, monthLabel, sourceName } from './qbSyncUtils';
import { needsQboReconnect, startQboReconnect } from './qboReconnect';

interface Props {
    branchId: number;
}

/**
 * Dashboard: "Re-sync from QuickBooks Online / Desktop" for a branch that has finished
 * its first QuickBooks import. Renders nothing for branches not connected to
 * QuickBooks (or not imported yet). An open re-sync shows as a badge linking to it.
 */
const ReSyncPanel: React.FC<Props> = ({ branchId }) => {
    const { t } = useTranslation('scenes.QBSync');
    const navigate = useNavigate();
    const { getStatus, startSession } = useQbSyncApi();
    const [status, setStatus] = useState<any>(null);
    const [showModal, setShowModal] = useState<boolean>(false);
    const [starting, setStarting] = useState<boolean>(false);
    const [reconnecting, setReconnecting] = useState<boolean>(false);

    useEffect(() => {
        setStatus(null);
        if (!branchId) return;
        getStatus(branchId, (_m: string, resp: any) => setStatus(resp?.data?.data || null),
            () => setStatus(null));          // not a QuickBooks branch -> no button
    }, [branchId]); // eslint-disable-line react-hooks/exhaustive-deps

    const noBankMonth = status?.reason === 'qb_sync.qbd_no_bank_month_closed';
    if (!status || (!status.imported_through_month && !noBankMonth)) return null;

    const name = sourceName(status.source);
    const open = status.open_session;
    const reconnect = status.source === 'qbo' && needsQboReconnect(status.reason);
    const reviewUrl = (id: number) => URLS.QB_SYNC_REVIEW.replace(':sessionId', String(id));

    const openLabel = (): string => {
        if (!open) return '';
        if (open.status === 'collecting') {
            return status.source === 'qbd'
                ? t('panel.waitingConnector', 'Waiting for the desktop connector')
                : t('panel.collecting', 'Reading changes…');
        }
        if (open.status === 'applying') return t('panel.applying', 'Applying changes…');
        return t('panel.ready', 'Changes ready for review');
    };

    const start = (syncThrough: string) => {
        setStarting(true);
        startSession({ branch_id: branchId, sync_through: syncThrough, reconciled_confirmed: true },
            (_m: string, resp: any) => {
                setStarting(false);
                setShowModal(false);
                const id = resp?.data?.data?.id;
                if (id) navigate(reviewUrl(id));
            },
            (msg: string) => {
                setStarting(false);
                toast.error(msg);
            });
    };

    return (
        <div className="d-flex flex-column align-items-end gap-1">
            <div className="d-flex flex-wrap align-items-center justify-content-end gap-2">
                {open && (
                    <button type="button" className="btn btn-sm btn-outline-warning" onClick={() => navigate(reviewUrl(open.id))}>
                        {openLabel()} →
                    </button>
                )}
                {reconnect && (
                    <button type="button" className="btn btn-warning" disabled={reconnecting}
                            onClick={() => {
                                setReconnecting(true);
                                startQboReconnect(branchId, () => {
                                    setReconnecting(false);
                                    toast.error(String(t('panel.reconnectFailed', 'Could not open the QuickBooks Online sign-in. Please try again.')));
                                });
                            }}>
                        <i className="fas fa-plug me-2" aria-hidden="true"></i>
                        {t('panel.reconnect', 'Reconnect QuickBooks Online')}
                    </button>
                )}
                <button type="button" className="btn btn-primary"
                        disabled={!status.can_resync || !!open}
                        title={!status.can_resync && status.reason
                            ? String(t(`statusReason.${String(status.reason).replace('qb_sync.', '')}`, String(status.reason))) : undefined}
                        onClick={() => setShowModal(true)}>
                    <i className="fas fa-sync-alt me-2" aria-hidden="true"></i>
                    {t('panel.button', 'Re-sync from {{name}}', { name })}
                </button>
                <div className="ps-2 border-start text-muted text-nowrap text-end fw-normal"
                     style={{ fontSize: 12, lineHeight: '18px' }} data-testid="qbsync-meta">
                    {t('panel.lastSyncedLabel', 'Last synced')} <span className="text-body">{dateTimeLabel(status.last_sync_at) || '—'}</span>
                    <span className="mx-2" aria-hidden="true">|</span>
                    {t('panel.importedThroughLabel', 'Imported through')} <span className="text-body">{monthLabel(status.imported_through_month) || '—'}</span>
                </div>
            </div>
            {reconnect && (
                <small className="text-danger">
                    {t('statusReason.qbo_reconnect_required', 'Your QuickBooks Online connection has expired. Reconnect to re-sync.')}
                </small>
            )}
            {noBankMonth && (
                <small className="text-danger">
                    {t('statusReason.qbd_no_bank_month_closed', 'Close at least one bank month (bank reconciliation) before re-syncing from QuickBooks Desktop.')}
                </small>
            )}
            <ReSyncModal show={showModal} status={status} starting={starting}
                         onClose={() => setShowModal(false)} onStart={start} />
        </div>
    );
};

export default ReSyncPanel;
